import { useState, useEffect } from 'react';
import { X, Trophy } from 'lucide-react';
import { supabase } from '../lib/supabase';

export function FoundingDiverBanner({ userId }) {
  const [progress, setProgress] = useState(null);
  const [showBanner, setShowBanner] = useState(false);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const checkProgress = async () => {
      try {
        // Check if already dismissed this session
        if (sessionStorage.getItem('founding_diver_dismissed')) {
          setLoading(false);
          return;
        }

        // Get user's rating count
        const { data: ratings, error: ratingsError } = await supabase
          .from('ratings')
          .select('id')
          .eq('user_id', userId);

        if (ratingsError) {
          console.error('Error fetching ratings:', ratingsError);
          setLoading(false);
          return;
        }

        const ratingCount = ratings?.length || 0;

        // If already has 10+ ratings, check if they have the badge
        if (ratingCount >= 10) {
          const { data: userBadges } = await supabase
            .from('user_badges')
            .select('badge_id, badges(name)')
            .eq('user_id', userId);

          const hasFoundingBadge = userBadges?.some(
            ub => ub.badges?.name === 'Founding Diver'
          );

          if (hasFoundingBadge) {
            // Already earned, don't show
            setLoading(false);
            return;
          }
        }

        // Try to get founding member count (this might fail if function doesn't exist yet)
        let spotsLeft = 100;
        try {
          const { data: foundingCount } = await supabase.rpc('count_founding_members');
          if (foundingCount !== null) {
            spotsLeft = Math.max(0, 100 - foundingCount);
          }
        } catch (e) {
          // Function might not exist yet, use default
          console.log('count_founding_members not available yet');
        }

        // Only show if under 10 ratings and spots still available
        if (ratingCount < 10 && spotsLeft > 0) {
          setProgress({
            current: ratingCount,
            needed: 10 - ratingCount,
            spotsLeft: spotsLeft
          });
          setShowBanner(true);
        }
      } catch (error) {
        console.error('Error checking founding diver progress:', error);
      } finally {
        setLoading(false);
      }
    };

    if (userId) {
      checkProgress();
    } else {
      setLoading(false);
    }
  }, [userId]);

  const handleDismiss = () => {
    setShowBanner(false);
    sessionStorage.setItem('founding_diver_dismissed', 'true');
  };

  if (loading || !showBanner || !progress) return null;

  const progressPercent = (progress.current / 10) * 100;

  return (
    <div className="bg-gradient-to-r from-amber-700 to-amber-900 border-2 border-dive-amber rounded-xl p-4 mb-4 relative overflow-hidden">
      {/* Background decoration */}
      <div className="absolute top-0 right-0 w-32 h-32 bg-amber-500/10 rounded-full -translate-y-1/2 translate-x-1/2" />
      
      <button
        onClick={handleDismiss}
        className="absolute top-2 right-2 text-amber-300 hover:text-white transition-colors z-10"
      >
        <X size={18} />
      </button>

      <div className="flex items-start gap-3 relative">
        <div className="text-4xl flex-shrink-0">🏆</div>
        <div className="flex-1 min-w-0">
          <h3 className="font-display text-white text-lg mb-1">
            Become a Founding Diver!
          </h3>
          <p className="text-amber-100 text-sm mb-3">
            You're <strong className="text-white">{progress.needed} bars away</strong> from earning the exclusive Founding Diver badge. 
            Only the first 100 users to rate 10+ bars will get this!
          </p>
          
          {/* Progress bar */}
          <div className="flex items-center gap-3">
            <div className="flex-1 bg-amber-950/50 rounded-full h-2.5 overflow-hidden">
              <div 
                className="bg-white rounded-full h-2.5 transition-all duration-500"
                style={{ width: `${progressPercent}%` }}
              />
            </div>
            <span className="text-white text-sm font-bold whitespace-nowrap">
              {progress.current}/10
            </span>
          </div>
          
          <p className="text-amber-200 text-xs mt-2 flex items-center gap-1">
            <span>⏰</span>
            <span>Limited spots available</span>
          </p>
        </div>
      </div>
    </div>
  );
}

export default FoundingDiverBanner;

